import moment, {Moment} from 'moment';
import sortedLastIndex from 'lodash/sortedLastIndex';
import type {XAxisBreaksOptions} from 'highcharts';

import {formatDate} from 'common/util/date';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Timezone} from 'common/constants';
import {offsetForHighcharts} from 'common/util/date';

import {Annotation, AnnotationUser} from './types';

export function formatAnnotationUserName(user: AnnotationUser): string {
  return `${user.first_name} ${user.last_name && user.last_name[0]}`;
}

export const ANNOTATION_GROUP_DATE_CUSTOM_FORMATTING = {[TimeUnit.Day]: `ddd, MMM D`};

export function parseAnnotationDate(isoDate: string): Moment {
  // retain the timezone info in the iso date (for later formatting, etc...)
  // assume your browser is in PDT
  // moment(`2025-08-01T12:00:00-05:00`).format() -> 2025-08-01T10:00:00-07:00
  // moment.parseZone(`2025-08-01T12:00:00-05:00`).format() -> 2025-08-01T12:00:00-05:00
  return moment.parseZone(isoDate, moment.ISO_8601);
}

export function formatAnnotationDateForGroup(
  isoDate: string,
  {timezone, timeUnit}: {timezone: Timezone; timeUnit?: TimeUnit},
) {
  timeUnit = timeUnit ?? TimeUnit.Day;
  return formatDate(parseAnnotationDate(isoDate), {
    unit: timeUnit,
    displayRangeIfWeek: true,
    customFormatting: ANNOTATION_GROUP_DATE_CUSTOM_FORMATTING,
    timezone,
    hideTimezone: true,
  });
}

export function formatAnnotationDate(isoDate: string, {timezone, timeUnit}: {timezone: Timezone; timeUnit?: TimeUnit}) {
  const dateMoment = parseAnnotationDate(isoDate);
  return formatDate(dateMoment, {
    unit: timeUnit,
    customFormatting: {[timeUnit as string]: `MMM Do, YYYY h:mma`},
    timezone,
    hideTimezone: true,
    displayRangeIfWeek: false,
  });
}

export function groupAnnotations(
  utcOffset: number,
  timezone: string,
  annotations: Annotation[],
  ticks: string[],
  breaks?: XAxisBreaksOptions[],
): Map<number, Annotation[]> {
  const allAnnotations = new Map();

  // Pre-compute tick numbers and mapping of each break to its last tick before the break starts
  let breakToLastTick = null;
  if (breaks && breaks.length > 0) {
    const ticksAsNumbers = ticks.map((t) => parseInt(t));
    breakToLastTick = computeBreakToLastTickMap(ticksAsNumbers, breaks);
  }

  annotations.forEach((annotation) => {
    annotation.highchartsDate = offsetForHighcharts(moment(annotation.date), timezone, utcOffset);

    const annotationDateMS = moment.utc(annotation.highchartsDate).valueOf();
    let index = parseInt(ticks[sortedLastIndex(ticks, annotationDateMS.toString()) - 1]);

    // If breaks are provided, check if the annotation timestamp or grouped timestamp falls within a break
    // If it does, move it to the last visible datapoint before the break
    if (breaks && breakToLastTick) {
      const adjustedIndex = adjustTimestampForBreaks(annotationDateMS, index, breakToLastTick, breaks);
      if (adjustedIndex !== null) {
        index = adjustedIndex;
      }
    }

    if (allAnnotations.has(index)) {
      const annotationsAtTime = allAnnotations.get(index);
      annotationsAtTime.push(annotation);
      allAnnotations.set(index, annotationsAtTime);
    } else {
      allAnnotations.set(index, [annotation]);
    }
  });
  return allAnnotations;
}

/**
 * Pre-computes a map from each break to the last tick before that break starts.
 * This eliminates the need to search through all ticks for every annotation.
 * @param ticksAsNumbers - Array of all available timestamps (as numbers)
 * @param breaks - Array of break periods
 * @returns Map from break index to the last tick before that break
 */
function computeBreakToLastTickMap(ticksAsNumbers: number[], breaks: XAxisBreaksOptions[]): Map<number, number> {
  const breakToLastTick = new Map<number, number>();

  breaks.forEach((breakItem, breakIndex) => {
    if (breakItem.from === undefined) {
      return;
    }

    // Find the last tick before this break starts
    for (let i = ticksAsNumbers.length - 1; i >= 0; i--) {
      if (ticksAsNumbers[i] < breakItem.from) {
        breakToLastTick.set(breakIndex, ticksAsNumbers[i]);
        break;
      }
    }
  });

  return breakToLastTick;
}

/**
 * Adjusts a timestamp if it falls within any break period by moving it to the last
 * visible datapoint before the break (consistent with how annotations are normally
 * grouped to the datapoint before their timestamp).
 * @param annotationDateMS - The original annotation timestamp
 * @param groupedTimestamp - The timestamp where the annotation was grouped to
 * @param breakToLastTick - Pre-computed map from break index to last tick before break
 * @param breaks - Array of break periods
 * @returns The adjusted timestamp, or null if no adjustment needed
 */
function adjustTimestampForBreaks(
  annotationDateMS: number,
  groupedTimestamp: number,
  breakToLastTick: Map<number, number>,
  breaks: XAxisBreaksOptions[],
): number | null {
  // Check if either the annotation itself or its grouped timestamp falls within any break
  const breakIndex = breaks.findIndex((breakItem) => {
    // Skip breaks without both from and to defined
    if (breakItem.from === undefined || breakItem.to === undefined) {
      return false;
    }
    return (
      (annotationDateMS >= breakItem.from && annotationDateMS < breakItem.to) ||
      (groupedTimestamp >= breakItem.from && groupedTimestamp < breakItem.to)
    );
  });

  if (breakIndex === -1) {
    return null;
  }

  // Return the pre-computed last tick for this break
  return breakToLastTick.get(breakIndex) ?? null;
}
